import { useState, useEffect, useCallback } from "react";
import {
  getMarginAutocompleteFieldType,
  getMarginStrategyAutocomplete,
} from "../../API/marginRuleApi";

const MIN_MARGIN_SEARCH_LENGTH = 2;

/**
 * Custom hook for fetching margin rule autocomplete options based on scope type and search term.
 * @param {string} scopeType - The scope type: 'category', 'hq', or 'item'
 * @param {string} searchTerm - The scope value search term
 * @returns {Object} { options: Array, loading: boolean, refetch: Function }
 */
export const useMarginRuleOptions = (scopeType, searchTerm = "") => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadMarginRuleOptions({ scopeType, searchTerm });
      setOptions(data);
    } catch (error) {
      console.error("Error fetching margin autocomplete options:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [scopeType, searchTerm]);

  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();
    if (!getMarginAutocompleteFieldType(scopeType) || trimmedSearchTerm.length < MIN_MARGIN_SEARCH_LENGTH) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    const debounceTimeout = setTimeout(() => {
      fetchOptions();
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [fetchOptions, scopeType, searchTerm]);

  return {
    options,
    loading,
    refetch: fetchOptions,
  };
};

export const loadMarginRuleOptions = async ({ scopeType, searchTerm = "" } = {}) => {
  const trimmedSearchTerm = searchTerm.trim();
  const fieldType = getMarginAutocompleteFieldType(scopeType);

  if (!fieldType || trimmedSearchTerm.length < MIN_MARGIN_SEARCH_LENGTH) {
    return [];
  }

  const data = await getMarginStrategyAutocomplete({ fieldType, searchTerm: trimmedSearchTerm });

  return data || [];
};

export default useMarginRuleOptions;
